<template>
  <div>
    <!-- Change card buttons -->
    <div class="d-flex navigate-btn">
      <v-btn
        v-if="tab > 0"
        class="card-btn card-left-btn"
        :style="`background-color: ${config.main_color} !important`"
        @click="navigateCard('previous', message_id)"
        aria-label="card-navigate-left-btn"
      >
        <v-icon color="#ffffff">mdi-chevron-left</v-icon>
      </v-btn>
      <div></div>
      <v-btn
        v-if="tab < message.cards.length - 1"
        class="card-btn card-right-btn"
        :style="`background-color: ${config.main_color} !important`"
        @click="navigateCard('next', message_id)"
        aria-label="card-navigate-right-btn"
      >
        <v-icon color="#ffffff">mdi-chevron-right</v-icon>
      </v-btn>
    </div>

    <div class="d-flex cards-container" @touchstart="handleTouchStart" @touchmove="handleTouchMove">
      <div class="chat-widget elevated-card cards" v-for="(card, card_i) in cards" :key="card_i" :eager="true">
        <div class="d-flex" style="flex-direction: column">
          <div style="height: 10.25rem; width: 18.1875rem">
            <v-img :src="card.image_url" class="card-image" :alt="card.title" :eager="true">
              <template v-slot:placeholder>
                <v-skeleton-loader type="image" />
              </template>
            </v-img>
          </div>
          <div class="card-detail" v-if="visibility">
            <div class="card-title">{{ card.title }}</div>
            <div class="card-subtitle">{{ card.subtitle }}</div>
            <v-expand-transition hide-on-leave>
              <div v-if="tab >= card_i - 1 && tab <= card_i + 1">
                <div v-for="(button, btn_i) in card.buttons" :key="btn_i">
                  <div v-if="btn_i < item_showed[card_i]">
                    <button v-ripple class="chat-btn hover-btn text-none" style="margin-top: 0.5rem" :style="`color: ${config.main_color}`" outlined @click="click(button)">
                      <span :style="`color: ${config.main_color} !important`">
                        {{ button.title }}
                      </span>
                    </button>
                  </div>
                  <v-btn
                    v-else-if="btn_i === item_showed[card_i]"
                    class="chat-btn text-none"
                    style="width: 3rem; margin-top: 0.5rem"
                    :style="`color: ${config.main_color}`"
                    variant="outlined"
                    @click="addItem(card_i)"
                    aria-label="card-show-more-item-btn"
                  >
                    <v-icon class="plus-btn">mdi-plus</v-icon>
                  </v-btn>
                </div>
              </div>
            </v-expand-transition>
          </div>
        </div>
      </div>
      <div style="min-width: 2.25rem"></div>
    </div>
  </div>
</template>

<script setup>
// Import config
import global_config from '@/../config.json'
// Import modules
import { ref, watch, onMounted, nextTick, defineProps } from 'vue'
import { useStore } from 'vuex'

// Init modules
const store = useStore()

// props
const { message, message_id } = defineProps({
  message: Object,
  message_id: String
})

// Data
const config = ref(global_config)
const item_showed = ref([])
const item_to_add = ref([])
const cards = ref(message.cards)
const visibility = ref(true)
const tab = ref(0)
const xDown = ref(null)
const yDown = ref(null)

// Methods
const navigateCard = (direction, id) => {
  var message_element = document.getElementById(id)
  if (direction === 'previous') tab.value--
  else if (direction === 'next') tab.value++

  message_element.scrollLeft = tab.value * 307
}

const click = ({ type, title, value }) => {
  if (type === 'web_url') window.open(value, '_blank')
  else store.dispatch('sendMessageToSocket', { title, value })
}

const addItem = (index) => {
  item_showed.value[index] += item_to_add.value[index]
  visibility.value = false

  nextTick(() => {
    visibility.value = true
  })
}

const getTouches = (e) => {
  return e.touches || e.originalEvent.touches
}
const handleTouchStart = (e) => {
  const firstTouch = getTouches(e)[0]
  xDown.value = firstTouch.clientX
  yDown.value = firstTouch.clientY
}
const handleTouchMove = (e) => {
  if (!xDown.value || !yDown.value) {
    return
  }

  var xUp = e.touches[0].clientX
  var yUp = e.touches[0].clientY

  var xDiff = xDown.value - xUp
  var yDiff = yDown.value - yUp

  if (Math.abs(xDiff) > Math.abs(yDiff)) {
    if (xDiff > 0) {
      // right swipe
      if (tab.value < message.cards.length - 1) navigateCard('next', message_id)
    } else {
      // left swipe
      if (tab.value > 0) navigateCard('previous', message_id)
    }
  }

  // reset values
  xDown.value = null
  yDown.value = null
}
watch(tab, () => {
  var message_element = document.getElementById(message_id)
  message_element.scrollLeft = tab.value * 307
})

onMounted(() => {
  let showed_array = []
  let to_add_array = []

  const { default_num_of_buttons = null, default_num_of_elements = null, default_num_of_elements_to_add = null } = message
  const cards = message.cards

  // General default num of btns/elements
  const general_default_num = default_num_of_buttons ? default_num_of_buttons : default_num_of_elements ? default_num_of_elements : 2
  // General default num of btns/elements to add
  const general_default_num_to_add = default_num_of_elements_to_add ? default_num_of_elements_to_add : null

  cards.forEach((card) => {
    // If card have specific default num
    const { default_num_of_buttons = null, default_num_of_elements = null, default_num_of_elements_to_add = null } = card

    const card_default_num = default_num_of_buttons ? default_num_of_buttons : default_num_of_elements ? default_num_of_elements : general_default_num
    const card_default_num_to_add = default_num_of_elements_to_add ? default_num_of_elements_to_add : general_default_num_to_add ? general_default_num_to_add : card.buttons.length - card_default_num

    showed_array.push(card_default_num)
    to_add_array.push(card_default_num_to_add)
  })

  item_showed.value = showed_array
  item_to_add.value = to_add_array
})
</script>

<style scope>
.cards-container {
  flex-direction: row;
}

.cards {
  height: fit-content;
  width: 100%;
  margin-right: 1rem;
}

.card-image {
  height: 10.25rem;
  width: 100%;
  border-radius: 1rem 1rem 0 0;
}

.card-detail {
  padding: 0 1.5rem 1rem 1.5rem;
}

.card-title {
  max-width: 15.1875rem;
  white-space: initial;
  padding: 1rem 0 0.75rem 0;
  font-weight: 700;
  font-size: 1.125rem;
  line-height: 1.625rem;
  color: #1a1a1a;
}

.card-subtitle {
  max-width: 15.1875rem;
  white-space: initial;
  padding-bottom: 0.25rem;
  font-weight: 400;
  font-size: 0.9375rem;
  line-height: 1.125rem;
  color: #828282;
}

.list-item {
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
}

.list-plus {
  justify-content: center;
  padding: 1rem 0;
}
</style>
